'use client';

import { useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import { ArrowRight, Check, Copy, Mail } from 'lucide-react';
import { Button } from '@/components/sections/kit';
import { HUMAN_CHECK_FAILED, useHumanCheck } from '@/components/forms/useHumanCheck';
import { useFormGuard } from '@/components/forms/useFormGuard';
import type { SharedLinkCreated } from '@/lib/shorten';
import { claimLoginUrl, claimRegisterUrl, formatExpiry } from './shorten-promotion';

/** Locally-generated QR (qrcode-generator, already a dependency via
 *  /qr-codes) — replaces the old api.qrserver.com hotlink, so the freshly
 *  minted short URL never leaks to a third-party image service. */
function ShortLinkQr({ value }: { value: string }) {
  const qr = useMemo(() => {
    try {
      const code = qrcode(0, 'M');
      code.addData(value);
      code.make();
      const n = code.getModuleCount();
      let d = '';
      for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
          if (code.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
        }
      }
      return { d, n };
    } catch {
      return null;
    }
  }, [value]);

  if (!qr) return null;
  return (
    <svg
      viewBox={`-2 -2 ${qr.n + 4} ${qr.n + 4}`}
      className="h-12 w-12 text-brand-700"
      aria-hidden="true"
    >
      <path fill="currentColor" d={qr.d} />
    </svg>
  );
}

type EmailStatus = 'idle' | 'sending' | 'sent' | 'error';

/**
 * The hero's post-submit panel: the short URL first (copy + QR), then the two
 * ways to keep it past 30 days — claim it into a free account, or get an
 * emailed admin link (clicks + shut down).
 *
 * It used to withhold the URL behind the offer ("Keep control of it before we
 * show it", with a small "No thanks" escape). With barely any traffic, friction
 * at a stranger's first contact costs more than the extra sign-ups the gate
 * could buy, so the visitor gets what they asked for straight away and the
 * offer sits next to it, with the expiry date as the reason to take it.
 */
export default function ShortenResult({ result }: { result: SharedLinkCreated }) {
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);
  const { guardFields, HoneypotField } = useFormGuard();
  const human = useHumanCheck();

  const [copied, setCopied] = useState(false);
  const expiry = formatExpiry(result.expires_at);

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (emailStatus === 'sending') return;
    const trimmed = email.trim();
    if (!trimmed) return;
    const payload = { id: result.id, claim_token: result.claim_token, email: trimmed, ...guardFields() };
    setEmailStatus('sending');
    setEmailError(null);
    try {
      // This sends mail to the typed address, so every submission needs a
      // fresh Turnstile token (website#302).
      const turnstileToken = await human.getToken();
      if (!turnstileToken) throw new Error(HUMAN_CHECK_FAILED);
      const res = await fetch('/api/shorten/admin-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, turnstileToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not send the email. Please try again.');
      setEmailStatus('sent');
    } catch (err) {
      setEmailStatus('error');
      setEmailError(err instanceof Error ? err.message : 'Could not send the email.');
    } finally {
      human.reset();
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(result.short_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable (permissions) — the URL is right there to select */
    }
  };

  return (
    <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 text-left sm:p-5">
      {/* The link — what the visitor came for. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="rounded-lg border border-brand-200 bg-white p-2">
            <ShortLinkQr value={result.short_url} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">
              Your short link
            </p>
            <a
              href={result.short_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate font-mono font-bold text-brand-900 hover:underline"
            >
              {result.short_url}
            </a>
          </div>
        </div>
        <button
          type="button"
          onClick={copy}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* The offer — next to the link, never in front of it. */}
      <div className="mt-4 border-t border-brand-100 pt-4">
        <p className="text-sm font-bold text-slate-900">
          Free links expire after 30 days{expiry ? ` (this one on ${expiry})` : ''}. Keep it:
        </p>

        <div className="mt-3 flex flex-col gap-2.5 sm:flex-row sm:items-center">
          <Button
            href={claimRegisterUrl(result.claim_token)}
            iconRight={<ArrowRight size={15} />}
            className="px-4 py-2.5 text-sm"
          >
            Create a free account
          </Button>
          <span className="text-xs text-slate-500">
            The link moves into your account and never expires.{' '}
            <a
              href={claimLoginUrl(result.claim_token)}
              className="font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-600"
            >
              Already have an account?
            </a>
          </span>
        </div>

        {emailStatus === 'sent' ? (
          <div className="mt-3 flex items-start gap-2.5 text-sm text-slate-600">
            <Mail size={16} className="mt-0.5 shrink-0 text-brand-600" aria-hidden="true" />
            <p>
              <span className="font-semibold text-slate-900">Check your inbox.</span> We sent an
              admin link to <span className="font-semibold">{email}</span>: it opens a page where
              you can watch clicks and shut the link down.
            </p>
          </div>
        ) : (
          <form onSubmit={handleEmailSubmit} onFocus={human.arm} className="mt-3" noValidate>
            <HoneypotField />
            <p className="mb-2 text-xs text-slate-500">
              No account? Get an admin link by e-mail to see clicks or shut it down.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                aria-label="Email address"
                className="min-w-0 flex-grow rounded-lg border border-brand-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              />
              <button
                type="submit"
                disabled={emailStatus === 'sending'}
                className="flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-brand-200 bg-white px-4 py-2.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Mail size={15} />
                {emailStatus === 'sending' ? 'Sending…' : 'Email me an admin link'}
              </button>
            </div>
            {emailStatus === 'error' && emailError && (
              <p role="alert" className="mt-2 text-xs font-medium text-red-700">
                {emailError}
              </p>
            )}
            {human.widget}
          </form>
        )}
      </div>
    </div>
  );
}
